import type { ReactNode } from 'react'
import {
  Children,
  type ForwardedRef,
  forwardRef,
  isValidElement,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react'
import { useDeviceMedia } from '../../hooks/use-device-media'
import type { BaseModalHandle } from '../modal'
import { BaseModal, BaseModalHeader } from '../modal/base-modal'
import DragHandler from './DragHandler'
import styles from './Drawer.module.scss'
import type { DragInfoType, DrawerProps } from './types'

const checkChildIsType = (child: ReactNode, componentType: string) =>
  isValidElement(child) && child.type.name === componentType

const Drawer = forwardRef(function Drawer(
  { onDragStart, onDrag, onDragEnd, children, ...rest }: DrawerProps,
  ref?: ForwardedRef<unknown>
) {
  const { isDesktop } = useDeviceMedia()
  const modalRef = useRef<BaseModalHandle>(null)
  const initialHeight = useRef<number | null>(null)

  const setHeight = useCallback((height: string) => {
    const dialogRef = modalRef.current?.getDialogRef()
    if (!dialogRef?.current) return
    dialogRef.current.style.height = height
  }, [])

  useImperativeHandle(
    ref,
    () => ({
      setHeight(height: string) {
        setHeight(height)
      },
    }),
    [setHeight]
  )

  const handleDragStart = (info: DragInfoType) => {
    if (!modalRef.current) return
    initialHeight.current =
      modalRef.current.getDialogRef().current?.getBoundingClientRect().height ?? null

    if (onDragStart) {
      onDragStart(info)
    }
  }

  const handleDrag = (info: DragInfoType) => {
    if (initialHeight.current !== null && info?.endY && info?.startY) {
      const verticalOffset = info.endY - info.startY
      setHeight(initialHeight.current - verticalOffset + 'px')
    }
    if (onDrag) {
      onDrag(info)
    }
  }

  const handleDragEnd = (info: DragInfoType) => {
    initialHeight.current = null
    if (onDragEnd) {
      onDragEnd(info)
    }
  }

  if (isDesktop) {
    return null
  }

  const Header = Children.toArray(children).find((child) =>
    checkChildIsType(child, 'BaseModalHeader')
  )
  const Rest = Children.toArray(children).filter((child) =>
    checkChildIsType(child, 'BaseModalHeader')
  )
  const dragHandler = (
    <DragHandler onDrag={handleDrag} onDragStart={handleDragStart} onDragEnd={handleDragEnd} />
  )
  return (
    <BaseModal
      {...rest}
      className={styles['pintxo-drawer']}
      overlayClose={false}
      showCloseButton={false}
      ref={modalRef}
      withScrim={false}
      dragHandler={dragHandler}
    >
      <BaseModalHeader {...Header?.props}>{Header && Header.props.children}</BaseModalHeader>
      {Rest}
    </BaseModal>
  )
})

export default Drawer
